import { z } from 'zod';

/** Body of POST /api/chat: the conversation so far, ending with the user's question. */
export const chatRequestSchema = z.object({
  messages: z
    .array(
      z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string().trim().min(1).max(2000),
      }),
    )
    .min(1)
    .max(20)
    .refine((m) => m.at(-1)?.role === 'user', 'The last message must be the user question'),
});

export type ChatRequest = z.infer<typeof chatRequestSchema>;

/** Where the data of an answer comes from: shown under each answer. */
export interface ChatSource {
  source: string;
  url: string | null;
  /** Year of the data */
  year: number | null;
  updated_at: string | null;
  /** The data is from another year than the current season */
  is_reference: boolean;
}

/** The one brotherhood an answer was about: the interface suggests follow-ups on it. */
export interface ChatBrotherhood {
  name: string;
  /** Procession day ("Martes Santo"), if known */
  day: string | null;
}

/** Server-sent events of POST /api/chat, one JSON object per `data:` line. */
export type ChatEvent =
  /** A tool is being called (to show "consultando el horario…") */
  | { type: 'tool'; name: string }
  /** A piece of the answer text */
  | { type: 'delta'; text: string }
  /**
   * Replace the whole answer shown so far: the grounding guard found data that no tool
   * returned, and this is the corrected (or withdrawn) answer.
   */
  | { type: 'replace'; text: string }
  | {
      type: 'done';
      sources: ChatSource[];
      clock: { now: string; simulated: boolean };
      /** Present only when the tools resolved exactly one brotherhood */
      brotherhood?: ChatBrotherhood;
    }
  | { type: 'error'; message: string };

/** GET /api/context: what the interface needs to adapt to the date. */
export interface AppContext {
  now: string;
  /** The clock is simulated (SIMULATED_CLOCK): the interface must show it */
  simulated: boolean;
  season: {
    year: number;
    first_day: string;
    last_day: string;
    days: { label: string; date: string }[];
  };
  /** Procession days whose date is today ("Viernes Madrugada" and "Viernes Santo" share one) */
  today: string[];
  /** Procession days with processions on the street right now, by the schedule */
  in_progress: string[];
  /** Year of the latest schedule loaded; null if none */
  schedule_year: number | null;
}
